<header fxLayoutAlign="space-between center">
  <h2 class="nomargin">{{ dialogData.mode === 'create' ? 'Add a manual code' : 'Edit code' }}</h2>
  <ui-close [border]="true" (click)="dialogRef.close()"></ui-close>
</header>

<mat-spinner *ngIf="loading" diameter="24"></mat-spinner>

<div *ngIf="properties$ | async as properties">
  <div [class.disabled]="!properties">
    <form #codeForm="ngForm">
      <!-- property selector-->
      <!-- multiple properties if we're creating an ops code, or editing a code with the 'action' that allows us to define multiple properties -->
      <ng-container
        *ngIf="
          dialogData.mode === 'create' || allowableActions?.includes('can_attach_to_multiple_properties');
          else singlePropertySelectorTemplate
        ">
        <label class="block mb-1">Properties</label>
        <div
          class="multi-property-selector border border-smoke-darker rounded-md mb-4 p-2 max-h-48 overflow-y-auto flex flex-col">
          <mat-checkbox
            *ngFor="let property of properties"
            [checked]="isSelected(property)"
            [disabled]="
              (propertySelectionConstraintCodeLength &&
                property.code_length !== propertySelectionConstraintCodeLength) ||
              (propertySelectionConstraintTimezone && property.timezone !== propertySelectionConstraintTimezone)
            "
            [matTooltip]="
              (propertySelectionConstraintCodeLength &&
                property.code_length !== propertySelectionConstraintCodeLength) ||
              (propertySelectionConstraintTimezone && property.timezone !== propertySelectionConstraintTimezone)
                ? 'This property does not have the same code length or timezone as already selected properties.'
                : ''
            "
            (change)="onPropertyScopeChange($event, property)">
            <div fxLayout fxLayoutAlign="start center" fxLayoutGap="8px">
              <img class="w-6 h-5 rounded-sm" [src]="property.picture" [alt]="property.label" />
              <span>{{ property.label }}</span>
            </div>
          </mat-checkbox>
        </div>
      </ng-container>

      <!-- single property selection-->
      <ng-template #singlePropertySelectorTemplate>
        <div [class.disabled]="true">
          <label>Property</label>
          <mat-form-field class="fw">
            <mat-select [value]="selectedProperties[0]" placeholder="Select a property" name="input-property">
              <mat-select-trigger *ngIf="selectedProperties[0]">
                <div fxLayout fxLayoutAlign="start center" fxLayoutGap="8px">
                  <img width="16px" [src]="selectedProperties[0].picture" [alt]="selectedProperties[0].label" />
                  <span>{{ selectedProperties[0].label }}</span>
                </div>
              </mat-select-trigger>

              <mat-option *ngFor="let property of properties" [value]="property">
                <div fxLayout fxLayoutAlign="start center" fxLayoutGap="8px">
                  <img class="property-img" [src]="property.picture" [alt]="property.label" />
                  <span>{{ property.label }}</span>
                </div>
              </mat-option>
            </mat-select>
          </mat-form-field>
        </div>
      </ng-template>

      <!-- name -->
      <div
        [matTooltip]="
          dialogData.mode === 'edit' && !allowableActions?.includes('edit_can_change_name')
            ? 'Once a code is on the lock the name cannot be updated.'
            : ''
        ">
        <label>Name</label>
        <mat-form-field class="fw">
          <input
            matInput
            name="input-name"
            placeholder="What will this code be used for?"
            type="text"
            [(ngModel)]="name"
            required
            maxlength="50"
            [class.disabled]="dialogData.mode === 'edit' && !allowableActions?.includes('edit_can_change_name')" />
        </mat-form-field>
      </div>

      <!-- code-->
      <div *ngIf="selectedProperties.length > 0">
        <label>{{ selectedProperties[0].code_length }} digit code</label>
        <mat-form-field class="fw">
          <input
            matInput
            name="input-code"
            [attr.placeholder]="selectedProperties[0].code_length + ' digits'"
            type="text"
            [minlength]="selectedProperties[0].code_length"
            [attr.maxlength]="selectedProperties[0].code_length"
            [(ngModel)]="code"
            pattern="[0-9]+"
            required />
          <mat-error>The code should be an {{ selectedProperties[0].code_length }} digit code.</mat-error>
        </mat-form-field>
      </div>

      <!-- start and end times -->
      <div *ngIf="!alwaysOn" fxLayout fxLayoutGap="16px" fxLayout.lt-md="column">
        <div fxFlex>
          <label>Start</label>
          <mat-form-field class="fw nopadding" [matMenuTriggerFor]="dateSelectMenu" #dateMenuTrigger="matMenuTrigger">
            <input
              matInput
              name="input-start"
              type="text"
              [value]="
                (start.date | date) +
                ' - ' +
                (start.time.hours | number: '2.0-0') +
                ':' +
                (start.time.minutes | number: '2.0-0')
              "
              placeholder="Start date"
              required
              class="clickable" />
          </mat-form-field>

          <mat-menu #dateSelectMenu="matMenu">
            <ng-container
              *ngTemplateOutlet="
                dateTimePickerContent;
                context: { selectedDate: start.date, selectedTime: start.time, type: 'start', trigger: dateMenuTrigger }
              "></ng-container>
          </mat-menu>
        </div>

        <div fxFlex>
          <label>End</label>
          <mat-form-field
            class="fw nopadding"
            [matMenuTriggerFor]="endDateSelectMenu"
            #endDateMenuTrigger="matMenuTrigger">
            <input
              matInput
              name="input-end"
              type="text"
              [value]="
                (end.date | date) +
                ' - ' +
                (end.time.hours | number: '2.0-0') +
                ':' +
                (end.time.minutes | number: '2.0-0')
              "
              placeholder="End date"
              required
              class="clickable" />
          </mat-form-field>

          <mat-menu #endDateSelectMenu="matMenu">
            <ng-container
              *ngTemplateOutlet="
                dateTimePickerContent;
                context: { selectedDate: end.date, selectedTime: end.time, type: 'end', trigger: endDateMenuTrigger }
              "></ng-container>
          </mat-menu>
        </div>
      </div>

      <!-- Always on -->
      <div
        *ngIf="dialogData.mode === 'create' || allowableActions?.includes('can_be_ongoing')"
        class="always-on-container">
        <label *ngIf="alwaysOn">Start / End</label>
        <div class="fw always-on" [class.always-on-active-border]="alwaysOn">
          <mat-checkbox class="fw" [(ngModel)]="alwaysOn" name="alwaysOn">Always on</mat-checkbox>
        </div>
      </div>
    </form>

    <!-- actions -->
    <div fxLayoutAlign="end center">
      <sbnb-button
        [loading]="saving"
        [disabled]="!codeForm.valid || selectedProperties.length === 0"
        (clicked)="onSaveClicked()">
        Save
      </sbnb-button>
    </div>
  </div>
</div>

<ng-template
  #dateTimePickerContent
  let-date="selectedDate"
  let-time="selectedTime"
  let-type="type"
  let-trigger="trigger">
  <div fxLayout="column" fxLayoutAlign="center center" (click)="($event.preventDefault); $event.stopPropagation()">
    <div [class.disabled]="dialogData.mode !== 'create' && !allowableActions?.includes('edit_can_change_date')">
      <div
        *ngIf="!allowableActions?.includes('edit_can_change_date') || dialogData.mode === 'create'"
        class="text__xsmall text-center date-blocked-warning">
        Only the time can be changed on this code
      </div>

      <ui-date-picker
        [numMonths]="1"
        [checkInOutStyling]="false"
        [startDate]="date"
        [initialDateRange]="date"
        [preventPastDatesSelection]="true"
        (dateClicked)="type === 'start' ? startDateSelected($event) : endDateSelected($event)"></ui-date-picker>
    </div>

    <div [class.disabled]="dialogData.mode !== 'create' && !allowableActions?.includes('edit_can_change_time')">
      <sbnb-time-picker
        style="display: block; margin-top: -16px; padding-bottom: 24px"
        [hours]="time.hours"
        [minutes]="time.minutes"
        [inlineMode]="true"
        (timeUpdated)="type === 'start' ? startTimeSelected($event) : endTimeSelected($event)"></sbnb-time-picker>
    </div>

    <sbnb-button style="padding-bottom: 24px" size="mini" type="secondary" (clicked)="trigger.closeMenu()">
      Done
    </sbnb-button>
  </div>
</ng-template>
